import type { LandscapeScreen, LandscapeSystem } from '../types/landscape'

// THE MENU (owner, 2026-10-02): "three new screens will be our primary application … everything else will remain but it
// goes into a System Settings menu section, with sub sections as needed, so the app is simpler and easier to use at a
// glance." The API names each screen's group; "Home" is the three. Everything else files under System Settings in the
// old group order, with the Connect checklist where it was. Pure, so the split is tested without a shell.

export const HOME = 'Home'
export const SETTINGS_ORDER = ['Pipeline', 'Relationships', 'Meetings', 'Playbook', 'Your voice', 'Signals', 'Firm data', 'Connect']
/** Routes that live under System Settings without being a screen of the landscape. */
export const SETTINGS_ROUTES = ['/quality', '/jobs', '/mailbox', '/notes', '/landscape']

export interface NavItem { route: string; title: string }
export interface NavSplit { primary: NavItem[]; groups: [string, NavItem[]][] }

export function splitNav(screens: LandscapeScreen[], systems: LandscapeSystem[]): NavSplit {
  const connectRoutes = new Set(systems.map((s) => s.route).filter((r): r is string => r !== null))
  const items = screens
    .filter((s) => s.unlocked)
    // the Connect checklist IS the row for a system with a route of its own; the screen behind it is not listed twice
    .filter((s) => s.group !== 'Connect' || !connectRoutes.has(s.route))
    .map((s) => ({ group: s.group || 'Pipeline', route: s.route, title: s.title }))
  const primary = items.filter((i) => i.group === HOME).map(({ route, title }) => ({ route, title }))
  const groups = SETTINGS_ORDER
    .map((g) => [g, items.filter((i) => i.group === g).map(({ route, title }) => ({ route, title }))] as [string, NavItem[]])
    .filter(([g, list]) => list.length > 0 || (g === 'Connect' && systems.length > 0))
  return { primary, groups }
}

/** Does the page at this path live under System Settings? The longest route that prefixes the path decides; the
 *  landing matches only itself, so "/contacts/list" (the list, under settings) beats "/contacts" (the manager). */
export function inSettings(split: NavSplit, systems: LandscapeSystem[], pathname: string): boolean {
  const matches = (route: string) => (route === '/' ? pathname === '/' : pathname === route || pathname.startsWith(route + '/'))
  let best: { route: string; settings: boolean } | null = null
  const consider = (route: string, settings: boolean) => {
    if (!matches(route)) return
    if (!best || route.length > best.route.length) best = { route, settings }
  }
  for (const it of split.primary) consider(it.route, false)
  for (const [, list] of split.groups) for (const it of list) consider(it.route, true)
  for (const s of systems) if (s.route) consider(s.route, true)
  for (const r of SETTINGS_ROUTES) consider(r, true)
  return best ? (best as { settings: boolean }).settings : false
}

const OPEN_KEY = 'scout.settings-open'

export function readSettingsOpen(): boolean {
  try { return typeof localStorage !== 'undefined' && localStorage.getItem(OPEN_KEY) === '1' } catch { return false }
}

export function writeSettingsOpen(open: boolean): void {
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(OPEN_KEY, open ? '1' : '0') } catch { /* a private window: the choice lasts the session */ }
}
